'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import Modal from '@/components/ui/Modal';
import Icon from '@/components/ui/AppIcon';
import { toast } from 'sonner';

interface AddItemFormData {
  itemCode: string;
  itemName: string;
  category: string;
  uom: string;
  reorderLevel: number;
  safetyStock: number;
  unitCost: number;
  hsnCode: string;
  barcode: string;
  shelfLifeDays: number;
  isQhseItem: boolean;
  hasExpiry: boolean;
  isActive: boolean;
  notes: string;
}

const CATEGORIES = ['RAW_MATERIAL', 'CONSUMABLE', 'SPARE_PART', 'TOOL', 'SAFETY_ITEM', 'PPE', 'FINISHED_GOOD', 'OTHER'];
const UOMS = ['EA', 'KG', 'L', 'M', 'MT', 'BAG', 'PR', 'SET', 'BOX', 'RLL', 'SHT'];

export default function AddItemModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { register, handleSubmit, watch, reset, formState: { errors } } = useForm<AddItemFormData>({
    defaultValues: { isActive: true, isQhseItem: false, hasExpiry: false, category: 'CONSUMABLE', uom: 'EA' },
  });

  const hasExpiry = watch('hasExpiry');
  const isQhseItem = watch('isQhseItem');

  const onSubmit = async () => {
    setIsSubmitting(true);
    // BACKEND INTEGRATION POINT: POST /api/v1/inventory/items
    await new Promise((r) => setTimeout(r, 1200));
    setIsSubmitting(false);
    toast.success('Item created successfully and added to the master catalog.');
    reset();
    onClose();
  };

  const fieldClass = (hasError: boolean) =>
    `w-full px-3 py-2 border rounded-lg text-sm bg-input text-foreground placeholder:text-muted-foreground/60 outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring transition-all ${
      hasError ? 'border-red-400 bg-red-50/20' : 'border-border hover:border-muted-foreground/40'
    }`;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add New Item"
      subtitle="Create a new item in the master catalog"
      size="xl"
      footer={
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            <span className="text-red-500">*</span> Required fields
          </p>
          <div className="flex gap-3">
            <button onClick={onClose} className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-muted transition-colors">
              Cancel
            </button>
            <button
              onClick={handleSubmit(onSubmit)}
              disabled={isSubmitting}
              className="flex items-center gap-2 px-5 py-2 bg-accent text-accent-foreground rounded-lg text-sm font-600 hover:bg-amber-500 transition-all active:scale-95 disabled:opacity-60"
              style={{ minWidth: '140px', justifyContent: 'center' }}
            >
              {isSubmitting ? (
                <span className="w-4 h-4 border-2 border-accent-foreground/30 border-t-accent-foreground rounded-full animate-spin" />
              ) : (
                <>
                  <Icon name="PlusIcon" size={15} />
                  Create Item
                </>
              )}
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Section 1: Identification */}
        <div>
          <h4 className="text-xs font-600 uppercase tracking-widest text-muted-foreground mb-4 flex items-center gap-2">
            <Icon name="IdentificationIcon" size={14} />
            Item Identification
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-500 text-foreground mb-1.5">
                Item Code <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-muted-foreground mb-1.5">Unique code used for GRN/GI transactions and barcoding</p>
              <input
                type="text"
                placeholder="e.g. LUB-HYD-46"
                className={fieldClass(!!errors.itemCode)}
                {...register('itemCode', { required: 'Item code is required.', pattern: { value: /^[A-Z0-9\-]+$/, message: 'Only uppercase letters, numbers, and hyphens.' } })}
              />
              {errors.itemCode && <p className="mt-1 text-xs text-red-600">{errors.itemCode.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-500 text-foreground mb-1.5">
                Item Name <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-muted-foreground mb-1.5">Full descriptive name shown in reports and issuance records</p>
              <input
                type="text"
                placeholder="e.g. Hydraulic Oil ISO 46"
                className={fieldClass(!!errors.itemName)}
                {...register('itemName', { required: 'Item name is required.', minLength: { value: 3, message: 'Minimum 3 characters.' } })}
              />
              {errors.itemName && <p className="mt-1 text-xs text-red-600">{errors.itemName.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-500 text-foreground mb-1.5">
                Category <span className="text-red-500">*</span>
              </label>
              <select className={fieldClass(!!errors.category)} {...register('category', { required: true })}>
                {CATEGORIES.map((c) => (
                  <option key={`cat-opt-${c}`} value={c}>{c.replace('_', ' ')}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-500 text-foreground mb-1.5">
                Unit of Measure (UOM) <span className="text-red-500">*</span>
              </label>
              <select className={fieldClass(!!errors.uom)} {...register('uom', { required: true })}>
                {UOMS.map((u) => (
                  <option key={`uom-opt-${u}`} value={u}>{u}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <hr className="border-border" />

        {/* Section 2: Stock Control */}
        <div>
          <h4 className="text-xs font-600 uppercase tracking-widest text-muted-foreground mb-4 flex items-center gap-2">
            <Icon name="ChartBarIcon" size={14} />
            Stock Control Parameters
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-500 text-foreground mb-1.5">
                Reorder Level <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-muted-foreground mb-1.5">Alert triggered when on-hand drops below this</p>
              <input
                type="number"
                min="0"
                placeholder="50"
                className={fieldClass(!!errors.reorderLevel)}
                {...register('reorderLevel', { required: 'Required.', min: { value: 0, message: 'Must be ≥ 0.' }, valueAsNumber: true })}
              />
              {errors.reorderLevel && <p className="mt-1 text-xs text-red-600">{errors.reorderLevel.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-500 text-foreground mb-1.5">
                Safety Stock <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-muted-foreground mb-1.5">Minimum buffer — issuance blocked below this level</p>
              <input
                type="number"
                min="0"
                placeholder="20"
                className={fieldClass(!!errors.safetyStock)}
                {...register('safetyStock', { required: 'Required.', min: { value: 0, message: 'Must be ≥ 0.' }, valueAsNumber: true })}
              />
              {errors.safetyStock && <p className="mt-1 text-xs text-red-600">{errors.safetyStock.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-500 text-foreground mb-1.5">
                Unit Cost (€) <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-muted-foreground mb-1.5">Initial cost — updated automatically by weighted average on GRN approval</p>
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                className={fieldClass(!!errors.unitCost)}
                {...register('unitCost', { required: 'Required.', min: { value: 0, message: 'Must be ≥ 0.' }, valueAsNumber: true })}
              />
              {errors.unitCost && <p className="mt-1 text-xs text-red-600">{errors.unitCost.message}</p>}
            </div>
          </div>
        </div>

        <hr className="border-border" />

        {/* Section 3: Compliance & Traceability */}
        <div>
          <h4 className="text-xs font-600 uppercase tracking-widest text-muted-foreground mb-4 flex items-center gap-2">
            <Icon name="ShieldCheckIcon" size={14} />
            Compliance & Traceability
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-500 text-foreground mb-1.5">HSN Code</label>
              <p className="text-xs text-muted-foreground mb-1.5">Harmonised System Nomenclature — required for tax compliance</p>
              <input type="text" placeholder="e.g. 27101960" className={fieldClass(false)} {...register('hsnCode')} />
            </div>
            <div>
              <label className="block text-sm font-500 text-foreground mb-1.5">Barcode / EAN</label>
              <p className="text-xs text-muted-foreground mb-1.5">Used for barcode scanning during GRN receiving and stock count</p>
              <input type="text" placeholder="e.g. 8901234500001" className={fieldClass(false)} {...register('barcode')} />
            </div>
          </div>

          {/* Toggles */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <label className="flex items-start gap-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-muted/40 transition-colors">
              <input type="checkbox" className="mt-0.5 w-4 h-4 rounded border-border accent-primary" {...register('isQhseItem')} />
              <div>
                <p className="text-sm font-500 text-foreground flex items-center gap-1.5">
                  <Icon name="ShieldCheckIcon" size={14} className="text-teal" />
                  QHSE Item
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">Enables compliance tracking, inspection scheduling, and expiry alerts</p>
              </div>
            </label>
            <label className="flex items-start gap-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-muted/40 transition-colors">
              <input type="checkbox" className="mt-0.5 w-4 h-4 rounded border-border accent-primary" {...register('hasExpiry')} />
              <div>
                <p className="text-sm font-500 text-foreground flex items-center gap-1.5">
                  <Icon name="ClockIcon" size={14} className="text-amber-600" />
                  Has Expiry Date
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">Batch-level expiry tracking on each GRN receipt</p>
              </div>
            </label>
            <label className="flex items-start gap-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-muted/40 transition-colors">
              <input type="checkbox" className="mt-0.5 w-4 h-4 rounded border-border accent-primary" {...register('isActive')} />
              <div>
                <p className="text-sm font-500 text-foreground flex items-center gap-1.5">
                  <Icon name="CheckCircleIcon" size={14} className="text-emerald-600" />
                  Active
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">Inactive items are hidden from GRN/GI entry screens</p>
              </div>
            </label>
          </div>

          {(hasExpiry || isQhseItem) && (
            <div className="mt-4">
              <label className="block text-sm font-500 text-foreground mb-1.5">Shelf Life (days)</label>
              <p className="text-xs text-muted-foreground mb-1.5">Used to auto-calculate expiry date from manufacture date on GRN receipt</p>
              <input
                type="number"
                min="1"
                placeholder="365"
                className={`w-40 ${fieldClass(false)}`}
                {...register('shelfLifeDays', { min: { value: 1, message: 'Must be ≥ 1 day.' }, valueAsNumber: true })}
              />
            </div>
          )}
        </div>

        <hr className="border-border" />

        {/* Section 4: Notes */}
        <div>
          <label className="block text-sm font-500 text-foreground mb-1.5">Internal Notes</label>
          <textarea
            rows={3}
            placeholder="Storage conditions, handling requirements, approved vendor notes…"
            className={`${fieldClass(false)} resize-none`}
            {...register('notes')}
          />
        </div>
      </div>
    </Modal>
  );
}